import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight, ChevronRight, Filter } from 'lucide-react';
import { trackPageView, trackCTAClick } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categories = ['All', 'Cement', 'Construction Chemicals', 'Blocks', 'Supplementary Materials'];

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [clickedProducts, setClickedProducts] = useState(new Set());
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    trackPageView('/products', 'Products');
    
    fetch(`${BACKEND_URL}/api/products`)
      .then(res => res.json())
      .then(data => setProducts(data))
      .catch(err => console.error('Error fetching products:', err));
  }, []);

  const handleProductClick = (productId) => {
    const newClicked = new Set(clickedProducts);
    newClicked.add(productId);
    setClickedProducts(newClicked);

    if (newClicked.size >= 9) {
      window.dispatchEvent(new CustomEvent('explorerEggTriggered', { 
        detail: { code: 'BUILDER12' } 
      }));
    }
  };

  const filteredProducts = activeCategory === 'All' 
    ? products 
    : products.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-void" data-testid="products-page">
      <SEOHead
        title="Products"
        description="Explore 12 premium construction products by Avi Cement — Lion Classic & Supreme cement, Hathi heavy-duty cement, Siddhee value cement, EFCON AAC blocks, Cemtite tile adhesives, ready-mix plaster, block adhesive, GGBS, and fly ash."
        path="/products"
      />
      {/* Hero Section */}
      <section className="relative py-12 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(243,100,88,0.1) 0%, transparent 60%)' }}
        />
        
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-coral font-mono text-sm mb-4 block">OUR PRODUCTS</span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Premium building<br />
              <span className="text-gradient">materials</span>
            </h1>
            <p className="text-gray-400 text-lg md:text-xl max-w-2xl leading-relaxed">
              From foundation to finish, explore our complete range of cement, blocks, and construction chemicals engineered for excellence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-6 bg-surface sticky top-20 z-40 border-y border-white/5">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-hide">
            <Filter className="w-5 h-5 text-gray-500 flex-shrink-0" />
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                data-testid={`filter-${category.toLowerCase()}`}
                className={`px-5 py-2 rounded-full font-display text-sm whitespace-nowrap transition-all ${
                  activeCategory === category
                    ? 'bg-coral text-white'
                    : 'bg-surface-light text-gray-400 hover:text-white hover:bg-white/10'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section ref={ref} className="py-16 md:py-24 bg-void">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/products/${product.id}`}
                  onClick={() => {
                    handleProductClick(product.id);
                    trackCTAClick(`view_product_${product.id}`, 'products_grid');
                  }}
                  data-testid={`product-card-${product.id}`}
                  className="group block bg-surface border border-white/5 hover:border-white/20 rounded-2xl overflow-hidden transition-all duration-300 h-full card-lift"
                >
                  {/* Product Header with Color/Image */}
                  <div 
                    className="h-40 relative overflow-hidden"
                    style={{ 
                      background: `linear-gradient(135deg, ${product.color}20 0%, transparent 100%)` 
                    }}
                  >
                    {product.image ? (
                      <img 
                        src={product.image} 
                        alt={`${product.name} - ${product.tagline || product.category} by Avi Cement`} 
                        loading="lazy"
                        width="280"
                        height="280"
                        className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div 
                        className="absolute bottom-4 left-6 w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${product.color}30` }}
                      >
                        <div 
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: product.color }}
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <span className="text-xs font-mono text-gray-500 uppercase tracking-wider">
                      {product.category}
                    </span>
                    
                    <h3 className="font-display text-xl font-semibold text-white mt-2 mb-3 group-hover:text-coral transition-colors">
                      {product.name}
                    </h3>
                    
                    <p className="text-gray-500 text-sm mb-6 line-clamp-2">
                      {product.tagline}
                    </p>
                    
                    <div className="flex items-center gap-2 text-coral text-sm font-display">
                      View details
                      <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No products found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-surface border-t border-white/5">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">
                Need a custom solution?
              </h3>
              <p className="text-gray-400">
                Contact us for bulk orders and special requirements.
              </p>
            </div>
            <Link
              to="/contact"
              className="group flex items-center gap-2 bg-coral hover:bg-coral-dark text-white font-display font-medium px-6 py-3 rounded-full transition-all"
            >
              Get in Touch
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
